import { useEffect, useState } from 'react';
import { InkBlock, ScoreChip } from '@/components/atoms';
import {
  scoreToModifier,
  STAT_IDS,
  type StatId,
  type StatScore,
  type Stats,
} from '@/schemas/stats';
import { STAT_META, formatModifier } from '@/utils/statMeta';
import { cn } from '@/lib/utils';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';
import { isStatsComplete, removeScore, stealAssign, type PartialStats } from './statsAssignment';

/**
 * Punteggi standard di Dungeon World — assegnazione "punti fissi" 16/15/13/12/9/8.
 * Ogni valore va usato esattamente una volta.
 */
const STAT_POOL: readonly StatScore[] = [16, 15, 13, 12, 9, 8];

/**
 * Step 4 — Caratteristiche (16/15/13/12/9/8).
 *
 * **FI-54 (2026-05-19)**: ex drag&drop @dnd-kit rimosso. Sostituito da
 * pattern **click-based unico** (mobile + desktop) per fix UX mobile
 * (Francesco confermò drag&drop su touch non funzionale anche con
 * TouchSensor delay 250ms).
 *
 * **Grammatica del flow**:
 *  1. Tap chip pool → diventa active (variant `selected`).
 *  2. Tap stat tile → `stealAssign` assegna a quella stat (eventuale chip
 *     precedente torna nel pool).
 *  3. Tap chip già assegnato in stat → diventa active (per riassegnare ad
 *     altra stat).
 *  4. Tap area pool quando chip è active → `removeScore` (chip torna pool).
 *  5. Esc / tap fuori → deseleziona.
 *
 * **Layout mobile-first**: pool 1-row sopra + stat grid 3×2 compatto.
 * Tutto sta in 1 schermata su iPhone SE 375×812 (vincolo Francesco). Tap
 * target 110×80 per stat tile, 48×48 per chip pool. Gap 16px fra tile
 * (anti-misstap).
 *
 * **Pure functions invariate**: `stealAssign` / `removeScore` /
 * `isStatsComplete` riusate identiche (zero rischio regressione,
 * test 2120/2120 invariati).
 */
export function SectionStats() {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();

  const [partial, setPartial] = useState<PartialStats>(() => initialPartialFromDraft(draft.stats));
  const [selectedScore, setSelectedScore] = useState<StatScore | null>(null);

  useEffect(() => {
    if (isStatsComplete(partial)) {
      dispatch({ type: 'SET_STATS', stats: partial as Stats });
      return;
    }
    // **FI-69**: partial incompleto (es. l'utente ha fatto switch e una stat
    // è tornata nel pool) → invalida draft.stats nel context. Senza questo
    // dispatch il context conserverebbe il valore completo precedente e la
    // sezione resterebbe marcata "completata" (predicate isSectionComplete
    // legge draft.stats !== undefined).
    if (draft.stats !== undefined) {
      dispatch({ type: 'CLEAR_STATS' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partial]);

  // Esc deseleziona — pattern coerente con Dialog/Modal del codebase.
  useEffect(() => {
    if (selectedScore === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setSelectedScore(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedScore]);

  function handlePoolChipClick(score: StatScore) {
    // Tap su chip pool toggle-selection (se già active, deseleziona).
    setSelectedScore((prev) => (prev === score ? null : score));
  }

  function handleStatTileClick(statId: StatId) {
    const currentScore = partial[statId];
    if (selectedScore !== null) {
      // Caso 1: chip active → assegna a questa stat (steal se serve).
      setPartial((prev) => stealAssign(prev, statId, selectedScore));
      setSelectedScore(null);
      return;
    }
    if (currentScore !== undefined) {
      // Caso 2: nessun chip active + tile pieno → seleziona il chip della tile
      // (l'utente può ora trasferirlo a un'altra stat o tappare il pool per rimuovere).
      setSelectedScore(currentScore);
      return;
    }
    // Caso 3: nessun chip active + tile vuoto → no-op (hint testuale guida).
  }

  function handlePoolAreaClick() {
    // Tap area pool quando chip active = rimuovi assegnazione di quel score
    // (se il chip era in una stat). Operazione idempotente: removeScore
    // ritorna lo stesso shape se score non era assegnato.
    if (selectedScore === null) return;
    setPartial((prev) => removeScore(prev, selectedScore));
    setSelectedScore(null);
  }

  const usedScores = new Set(Object.values(partial));
  const hintText = computeHintText(selectedScore);

  return (
    /* **FI-72**: `id="section-stats-compact"` anchor per scroll target che
     *  salta direttamente al "pezzo utile" (pool + stat-grid) bypassando
     *  l'header lungo della sezione. Su mobile 375 entra perfettamente in
     *  1 schermata. `scroll-mt-32` replica l'offset applicato al wrapper
     *  esterno `<section>` in `BuilderPage` (compensa AppTopbar 56px +
     *  StepNav ~80px sticky che altrimenti coprirebbero il top del target).
     *  Usato da `BuilderPage.scrollToSection` per id `'stats'`. */
    <div id="section-stats-compact" className="flex scroll-mt-32 flex-col gap-4">
      <InkBlock variant="full">Punteggi disponibili</InkBlock>
      <ValuePool
        usedScores={usedScores}
        selectedScore={selectedScore}
        onChipClick={handlePoolChipClick}
        onPoolAreaClick={handlePoolAreaClick}
      />
      <p
        aria-live="polite"
        className="text-ink-soft min-h-[1.5em] text-center text-sm italic sm:text-base"
      >
        {hintText}
      </p>
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {STAT_IDS.map((statId) => (
          <StatTile
            key={statId}
            statId={statId}
            score={partial[statId]}
            isSelected={selectedScore !== null && partial[statId] === selectedScore}
            highlightAsTarget={selectedScore !== null}
            onClick={() => handleStatTileClick(statId)}
          />
        ))}
      </div>
    </div>
  );
}

function computeHintText(selectedScore: StatScore | null): string {
  if (selectedScore === null) {
    return 'Tocca un valore in alto, poi una caratteristica per assegnarlo.';
  }
  return `Hai selezionato ${selectedScore}: tocca una caratteristica per assegnarlo, o il pool per rimettere il valore.`;
}

// ---------------------------------------------------------------------------
// Sub-components page-specific
// ---------------------------------------------------------------------------

interface ValuePoolProps {
  usedScores: ReadonlySet<StatScore>;
  selectedScore: StatScore | null;
  onChipClick: (score: StatScore) => void;
  onPoolAreaClick: () => void;
}

// page-specific, not reusable: container del pool 6-chip con gestione click
// area (remove on click quando chip è active). Tap target ~352×72 fits 375.
function ValuePool({ usedScores, selectedScore, onChipClick, onPoolAreaClick }: ValuePoolProps) {
  const isRemoveTarget = selectedScore !== null;
  return (
    <div
      role={isRemoveTarget ? 'button' : 'group'}
      aria-label={
        isRemoveTarget
          ? `Tocca per rimuovere il valore ${selectedScore} dal personaggio`
          : 'Punteggi disponibili — tocca un valore per selezionarlo'
      }
      onClick={(e) => {
        // Click sul chip → handler chip. Click sull'area background → remove.
        if (e.target === e.currentTarget && isRemoveTarget) onPoolAreaClick();
      }}
      className={cn(
        'border-rule-strong bg-paper-soft flex flex-wrap items-center justify-center gap-3 border border-dashed p-3 transition-colors sm:p-4',
        isRemoveTarget && 'border-ink-soft border-solid hover:border-ink hover:bg-paper-deep/40',
      )}
    >
      {STAT_POOL.map((score) => (
        <PoolChip
          key={score}
          score={score}
          assigned={usedScores.has(score)}
          isSelected={selectedScore === score}
          onClick={() => onChipClick(score)}
        />
      ))}
    </div>
  );
}

interface PoolChipProps {
  score: StatScore;
  assigned: boolean;
  isSelected: boolean;
  onClick: () => void;
}

// page-specific, not reusable: chip del pool con 3 stati (default / selected /
// disabled). Stop propagation evita che il click sul chip triggeri anche il
// click su ValuePool (remove area).
function PoolChip({ score, assigned, isSelected, onClick }: PoolChipProps) {
  const variant = computePoolChipVariant(assigned, isSelected);
  return (
    <ScoreChip
      score={score}
      variant={variant}
      mod={variant === 'pool-disabled' ? undefined : scoreToModifier(score)}
      onClick={(e) => {
        e.stopPropagation();
        if (!assigned) onClick();
      }}
      disabled={assigned}
      aria-pressed={isSelected}
      aria-label={ariaLabelForPoolChip(score, assigned, isSelected)}
    />
  );
}

function computePoolChipVariant(assigned: boolean, isSelected: boolean) {
  if (isSelected) return 'selected';
  if (assigned) return 'pool-disabled';
  return 'pool';
}

function ariaLabelForPoolChip(score: StatScore, assigned: boolean, isSelected: boolean): string {
  if (assigned) return `Valore ${score} — assegnato`;
  if (isSelected) return `Valore ${score} — selezionato, tocca una caratteristica`;
  return `Valore ${score} — tocca per selezionarlo`;
}

interface StatTileProps {
  statId: StatId;
  score: StatScore | undefined;
  isSelected: boolean;
  highlightAsTarget: boolean;
  onClick: () => void;
}

// page-specific, not reusable: tile compatto della caratteristica per il grid
// 3-col × 2-row. 110×80 mobile (tap target abbondante, sopra HIG 44×44).
// Label "FOR" su mobile, "FOR — Forza" su tablet+ via responsive Tailwind.
function StatTile({ statId, score, isSelected, highlightAsTarget, onClick }: StatTileProps) {
  const meta = STAT_META[statId];
  const isEmpty = score === undefined;
  const mod = score !== undefined ? scoreToModifier(score) : undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isSelected}
      aria-label={ariaLabelForTile(meta, score, highlightAsTarget)}
      className={cn(
        'font-display flex min-h-[5rem] flex-col items-center justify-center gap-1 border bg-paper-bright p-3 text-center transition-[border-color,background-color,box-shadow,transform] duration-150',
        isEmpty
          ? 'border-rule-strong border-dashed bg-transparent'
          : 'border-rule hover:border-ink',
        highlightAsTarget &&
          'border-ink-soft border-solid shadow-[inset_0_0_0_1px_var(--color-ink-soft)]',
        isSelected &&
          'border-ink bg-paper-soft scale-[1.03] shadow-[inset_0_0_0_2px_var(--color-ink)]',
      )}
    >
      <span className="text-ink-soft text-[0.7rem] tracking-[0.12em] uppercase">{meta.abbr}</span>
      <span className="text-[1.6rem] leading-none font-bold">{isEmpty ? '—' : score}</span>
      <span className="text-ink-soft text-[0.78rem]">
        {mod !== undefined ? formatModifier(mod) : '·'}
      </span>
      <span className="text-ink-faint hidden text-[0.7rem] font-normal tracking-normal normal-case italic sm:block">
        {meta.name}
      </span>
    </button>
  );
}

function ariaLabelForTile(
  meta: (typeof STAT_META)[StatId],
  score: StatScore | undefined,
  highlightAsTarget: boolean,
): string {
  if (score === undefined) {
    return highlightAsTarget
      ? `${meta.name} — vuota, tocca per assegnare il valore selezionato`
      : `${meta.name} — vuota`;
  }
  if (highlightAsTarget) {
    return `${meta.name} — punteggio ${score}, tocca per sostituire con il valore selezionato`;
  }
  return `${meta.name} — punteggio ${score}, tocca per selezionarla e spostarla`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function initialPartialFromDraft(stats: Stats | undefined): PartialStats {
  if (!stats) return {};
  return { ...stats };
}
